import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { getTagColor } from '../utils';

function UserStatistik({ user, setUser }) {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);

  useEffect(() => {
    loadStatistics();
  }, []);

  useEffect(() => {
    if (selectedSeason) {
      loadStatistics(selectedSeason);
    }
  }, [selectedSeason]);

  const loadStatistics = async (season = null) => {
    setLoading(true);
    try {
      const url = season ? `/users/me/statistics?season=${season}` : '/users/me/statistics';
      const res = await api.get(url);
      setStatistics(res.data);
      setAvailableSeasons(res.data.available_seasons || []);
      if (!selectedSeason && res.data.season) {
        setSelectedSeason(res.data.season);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} Min.`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  // Generate calendar URL (same logic as api.js for consistency)
  const getCalendarUrl = () => {
    if (!user?.calendar_token) return null;
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;

    let baseUrl;
    // Development: localhost with separate ports
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (port === '3000' || port === '') {
        baseUrl = `${protocol}//${hostname}:5000`;
      } else {
        baseUrl = `${protocol}//${hostname}:${port}`;
      }
    } else {
      // Production: same host
      baseUrl = `${protocol}//${hostname}`;
    }
    return `${baseUrl}/api/calendar/${user.calendar_token}.ics`;
  };

  const copyCalendarUrl = async () => {
    const url = getCalendarUrl();
    if (url) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const regenerateCalendarToken = async () => {
    if (!window.confirm('Wenn du den Link neu generierst, funktioniert der alte Link nicht mehr. Fortfahren?')) {
      return;
    }
    setRegenerating(true);
    try {
      const res = await api.post('/auth/regenerate-calendar-token');
      setUser({ ...user, calendar_token: res.data.calendar_token });
    } catch (error) {
      alert('Fehler beim Generieren des neuen Links');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Lädt...</div>;
  }

  if (!statistics) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Statistiken konnten nicht geladen werden.</div>;
  }

  const { summary, past_shifts, upcoming_shifts } = statistics;

  return (
    <div>
      <h2 style={{ marginBottom: '1.5rem' }}>Mein Profil</h2>

      {/* Team Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Team</h3>
        {user?.team ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {user.team.split(/[,;]\s*/).map((tag, idx) => {
              const trimmedTag = tag.trim();
              if (!trimmedTag) return null;
              const color = getTagColor(trimmedTag);
              return (
                <span key={idx} style={{
                  display: 'inline-block',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  backgroundColor: color.bg,
                  color: color.text
                }}>
                  {trimmedTag}
                </span>
              );
            })}
          </div>
        ) : (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            Kein Team zugewiesen
          </p>
        )}
      </div>

      {/* Helferpool Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Helferpool</h3>
        {user?.helferpool ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {user.helferpool.split(/;\s*/).map((tag, idx) => {
              const trimmedTag = tag.trim();
              if (!trimmedTag) return null;
              const color = getTagColor(trimmedTag);
              return (
                <span key={idx} style={{
                  display: 'inline-block',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  backgroundColor: color.bg,
                  color: color.text
                }}>
                  {trimmedTag}
                </span>
              );
            })}
          </div>
        ) : (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            Kein Helferpool zugewiesen
          </p>
        )}
      </div>

      {/* Calendar Subscription Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Kalender-Abo</h3>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Abonniere deine Einsätze in deiner Kalender-App (Google Calendar, Apple Kalender, Outlook, etc.)
        </p>
        {user?.calendar_token ? (
          <>
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'stretch',
              flexWrap: 'wrap'
            }}>
              <input
                type="text"
                value={getCalendarUrl() || ''}
                readOnly
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '0.75rem',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  marginBottom: 0
                }}
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={copyCalendarUrl}
                className="btn"
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: copied ? '#4caf50' : '#C62828',
                  whiteSpace: 'nowrap'
                }}
              >
                {copied ? 'Kopiert!' : 'Link kopieren'}
              </button>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#888' }}>
                Füge diesen Link als Kalender-Abo hinzu (nicht als Datei importieren)
              </span>
              <button
                onClick={regenerateCalendarToken}
                disabled={regenerating}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {regenerating ? 'Generiere...' : 'Neuen Link generieren'}
              </button>
            </div>
          </>
        ) : (
          <p style={{ color: '#888', fontStyle: 'italic' }}>
            Kalender-Link wird generiert...
          </p>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Meine Statistik</h3>
        {availableSeasons.length > 0 && (
          <select
            value={selectedSeason || ''}
            onChange={(e) => setSelectedSeason(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '0.9rem'
            }}
          >
            {availableSeasons.map(season => (
              <option key={season} value={season}>
                Saison {season}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div className="card" style={{ textAlign: 'center', backgroundColor: '#e3f2fd' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1565c0' }}>
            {summary.total_shifts}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Einsätze</div>
        </div>

        <div className="card" style={{ textAlign: 'center', backgroundColor: '#e8f5e9' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2e7d32' }}>
            {summary.past_shifts_count}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Geleistet</div>
        </div>

        <div className="card" style={{ textAlign: 'center', backgroundColor: '#fff3e0' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef6c00' }}>
            {summary.upcoming_shifts_count}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Anstehend</div>
        </div>

        <div className="card" style={{ textAlign: 'center', backgroundColor: '#fce4ec' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c2185b' }}>
            {summary.total_hours}h
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Stunden</div>
        </div>
      </div>

      {/* Upcoming Shifts */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{
          marginBottom: '1rem',
          color: '#ef6c00',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>&#9203;</span>
          Anstehende Einsätze ({summary.upcoming_shifts_count})
        </h3>

        {upcoming_shifts.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
            Keine anstehenden Einsätze.
            <br />
            <Link to="/" style={{ color: '#C62828' }}>Jetzt für einen Einsatz anmelden</Link>
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {upcoming_shifts.map((shift) => (
              <Link
                key={`${shift.event_id}-${shift.shift_id}`}
                to={`/event/${shift.event_id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fff3e0',
                  borderRadius: '8px',
                  border: '1px solid #ffcc80',
                  transition: 'transform 0.1s, box-shadow 0.1s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <strong style={{ color: '#e65100' }}>{shift.event_title}</strong>
                      <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                        {formatDate(shift.event_date)}
                      </div>
                      <div style={{ color: '#888', fontSize: '0.85rem' }}>
                        {shift.event_location}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#ef6c00',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}>
                        {shift.shift_name}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        {shift.start_time} Uhr • {formatDuration(shift.duration_minutes)}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Past Shifts */}
      <div className="card">
        <h3 style={{
          marginBottom: '1rem',
          color: '#2e7d32',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>&#10003;</span>
          Geleistete Einsätze ({summary.past_shifts_count})
          {summary.past_hours > 0 && (
            <span style={{
              fontWeight: 'normal',
              fontSize: '0.9rem',
              color: '#666',
              marginLeft: 'auto'
            }}>
              Total: {summary.past_hours}h
            </span>
          )}
        </h3>

        {past_shifts.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
            Noch keine geleisteten Einsätze.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {past_shifts.map((shift) => (
              <div
                key={`${shift.event_id}-${shift.shift_id}`}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  borderLeft: '4px solid #4caf50'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <strong>{shift.event_title}</strong>
                    <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                      - {shift.shift_name}
                    </span>
                    <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                      {formatDate(shift.event_date)} • {shift.event_location}
                    </div>
                  </div>
                  <div style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#2e7d32',
                    fontWeight: 'bold'
                  }}>
                    {formatDuration(shift.duration_minutes)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserStatistik;
